import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Link2, Unlink, CheckCircle2 } from "lucide-react";
import { formatApiError } from "@/auth/AuthContext";

function OrgPicker({ api, orgs, onDone }) {
  const [sel, setSel] = useState("");
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try { await axios.post(`${api}/xero/select-org`, { xero_tenant_id: sel }); toast.success("Xero connected"); onDone(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setBusy(false); }
  };
  return (
    <div className="rounded-xl border border-[var(--hp-gold)]/50 p-3 space-y-2" data-testid="xero-org-picker">
      <p className="text-xs text-[var(--hp-text)]">Your Xero login can see more than one organisation. Pick the one that belongs to <span className="font-semibold">this programme</span> — invoices and payments will be pushed there.</p>
      <select data-testid="xero-org-select" value={sel} onChange={(e) => setSel(e.target.value)} className="w-full rounded-lg border border-[var(--hp-border-hi)] bg-[var(--hp-bg)] px-3 py-2 text-sm text-[var(--hp-heading)]">
        <option value="">Choose organisation…</option>
        {orgs.map((o) => <option key={o.id} value={o.id}>{o.name || o.id}</option>)}
      </select>
      <button data-testid="xero-org-confirm" onClick={save} disabled={!sel || busy} className="inline-flex items-center gap-2 rounded-full bg-[#13B5EA] px-4 py-2 text-xs font-bold text-[var(--hp-heading)] disabled:opacity-40"><CheckCircle2 className="h-3.5 w-3.5" /> Use this organisation</button>
    </div>
  );
}

export default function XeroCard({ api, isAdmin }) {
  const [connecting, setConnecting] = useState(false);
  // asks the app for Xero's sign-in address first (so an expired sign-in is refreshed on the way) rather than sending the browser to the API cold
  const connect = async () => {
    if (connecting) return;
    setConnecting(true);
    try { const r = await axios.post(`${api}/xero/connect/start`); window.location.href = r.data.url; }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); setConnecting(false); }
  };
  const [s, setS] = useState(null);
  const load = () => axios.get(`${api}/xero/status`).then((r) => setS(r.data)).catch(() => {});
  useEffect(() => {
    load();
    const p = new URLSearchParams(window.location.search).get("xero");
    if (p === "connected") toast.success("Xero connected");
    else if (p === "choose") toast.info("Almost there — choose which Xero organisation to use");
    else if (p) toast.error(p === "noorg" ? "No Xero organisation was authorised" : "Xero connection failed — please try again");
    if (p) window.history.replaceState({}, "", window.location.pathname);
  }, []); // eslint-disable-line
  const disconnect = async () => { if (!window.confirm("Disconnect Xero for this provider?")) return; try { await axios.post(`${api}/xero/disconnect`); toast.success("Xero disconnected"); load(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };
  if (!s) return null;
  return (
    <div className="rounded-2xl border border-[var(--hp-border)] bg-[var(--hp-card)] p-5 space-y-3" data-testid="xero-card">
      <div className="flex items-start justify-between gap-3">
        <div><h3 className="font-head text-lg font-semibold text-[var(--hp-heading)] inline-flex items-center gap-2"><Link2 className="h-4 w-4 text-[#13B5EA]" /> Xero accounting</h3>
          <p className="text-xs text-[var(--hp-muted)] mt-1">Connect your own Xero organisation, then push invoices (and their payments) straight into Xero from the invoice list.</p></div>
        {s.connected && <span className="inline-flex items-center gap-1 rounded-full border border-[var(--hp-primary)]/40 px-2.5 py-0.5 text-xs font-semibold text-[var(--hp-profit)]" data-testid="xero-connected-badge"><CheckCircle2 className="h-3 w-3" /> Connected</span>}
      </div>
      {s.needsOrg && isAdmin && <OrgPicker api={api} orgs={s.orgs || []} onDone={load} />}
      {!s.configured
        ? <p className="text-xs text-[var(--hp-gold)]" data-testid="xero-not-configured">Xero isn't configured on this server yet.</p>
        : s.connected
          ? <div className="flex flex-wrap items-center gap-3 text-xs"><span className="text-[var(--hp-text)]" data-testid="xero-org">Organisation: <span className="text-[var(--hp-heading)] font-semibold">{s.orgName || "—"}</span>{s.connectedAt ? ` · since ${s.connectedAt}` : ""}</span>{isAdmin && <button data-testid="xero-disconnect" onClick={disconnect} className="inline-flex items-center gap-1.5 rounded-full border border-[var(--hp-loss)]/50 px-3 py-1.5 font-semibold text-[var(--hp-loss)] hover:bg-[var(--hp-loss)]/10"><Unlink className="h-3.5 w-3.5" /> Disconnect</button>}</div>
          : isAdmin
            ? <button data-testid="xero-connect" onClick={connect} disabled={connecting} className="disabled:opacity-50 inline-flex items-center gap-2 rounded-full bg-[#13B5EA] px-4 py-2 text-xs font-bold text-[var(--hp-heading)]"><Link2 className="h-3.5 w-3.5" /> Connect Xero</button>
            : <p className="text-xs text-[var(--hp-muted)]">Ask an admin to connect Xero.</p>}
    </div>
  );
}
